import React, { useState, useEffect, useCallback } from 'react';
import {
  Table, Button, Modal, Form, Input, InputNumber, message, Space, Card, Select, Dropdown, Grid
} from 'antd';
import { PlusOutlined, SyncOutlined, CloudSyncOutlined, LineChartOutlined, SettingOutlined, MoreOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { Vehicle, VehicleCapacityTons } from '../types';
import { vehicleApi, vehicleCapacityTonsApi, fuelEfficiencyApi } from '../services/api';
import { useLanguage } from '../contexts/LanguageContext';
import { getVehicleDesktopColumns, getVehicleMobileColumns } from '../components/vehicle/VehicleColumns';
import FuelEfficiencyModal from '../components/vehicle/FuelEfficiencyModal';
import AllVehiclesEfficiencyModal from '../components/vehicle/AllVehiclesEfficiencyModal';
import JourneySyncModal from '../components/vehicle/JourneySyncModal';
import { useVehicleSyncOperations } from '../hooks/useVehicleSyncOperations';

const { useBreakpoint } = Grid;

const VehiclePage: React.FC = () => {
  const { t } = useLanguage();
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [capacityTons, setCapacityTons] = useState<VehicleCapacityTons[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState<Vehicle | null>(null);
  const [form] = Form.useForm();

  // Fuel Efficiency History State
  const [efficiencyModalVisible, setEfficiencyModalVisible] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState<Vehicle | null>(null);
  const [efficiencyHistory, setEfficiencyHistory] = useState<any[]>([]);
  const [efficiencySummary, setEfficiencySummary] = useState<any>(null);
  const [loadingEfficiency, setLoadingEfficiency] = useState(false);

  // All Vehicles Efficiency State
  const [allVehiclesModalVisible, setAllVehiclesModalVisible] = useState(false);
  const [allVehiclesData, setAllVehiclesData] = useState<any>(null);
  const [loadingAllVehicles, setLoadingAllVehicles] = useState(false);
  const [recalculatingAllVehicles, setRecalculatingAllVehicles] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [vehiclesRes, capacityRes] = await Promise.all([
        vehicleApi.getAll(),
vehicleCapacityTonsApi.getAll()
      ]);
      setVehicles(vehiclesRes.data);
      setCapacityTons(capacityRes.data);
    } catch (error) {
    message.error(t('error.fetching.data'));
    } finally {
   setLoading(false);
    }
  }, [t]);

  // Use VCOMSAT sync operations hook
  const {
    syncingVehicles,
    syncingJourney,
    journeySyncModalVisible,
    journeyDateRange,
    setJourneyDateRange,
    setJourneySyncModalVisible,
    handleSyncVehicles,
    handleOpenJourneySyncModal,
    handleSyncJourneyForAllVehicles
  } = useVehicleSyncOperations({ t, isMobile, fetchData });

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleAdd = () => {
    setEditingVehicle(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (vehicle: Vehicle) => {
    setEditingVehicle(vehicle);
    form.setFieldsValue(vehicle);
    setModalVisible(true);
  };

  const handleDelete = async (id: number) => {
    try {
      await vehicleApi.delete(id);
      message.success(t('vehicle.deleted.successfully'));
  fetchData();
    } catch (error) {
      message.error(t('error.deleting.vehicle'));
    }
  };

  const handleSubmit = async (values: any) => {
    try {
      if (editingVehicle) {
        await vehicleApi.update(editingVehicle.id, values);
        message.success(t('vehicle.updated.successfully'));
 } else {
   await vehicleApi.create(values);
        message.success(t('vehicle.created.successfully'));
      }
      
      setModalVisible(false);
      form.resetFields();
      fetchData();
    } catch (error) {
      message.error(editingVehicle ? t('error.updating.vehicle') : t('error.creating.vehicle'));
    }
  };

  // Fuel Efficiency History Handlers
  const handleViewEfficiency = async (vehicle: Vehicle) => {
    setSelectedVehicle(vehicle);
    setEfficiencyModalVisible(true);
    setLoadingEfficiency(true);

    try {
      const [historyResponse, summaryResponse] = await Promise.all([
 fuelEfficiencyApi.getHistory(vehicle.id),
        fuelEfficiencyApi.getSummary(vehicle.id)
      ]);

      setEfficiencyHistory(historyResponse.data);
      setEfficiencySummary(summaryResponse.data);
    } catch (error: any) {
      message.error(error?.message || t('error.fetching.data'));
    } finally {
   setLoadingEfficiency(false);
    }
  };

  // All Vehicles Efficiency Handler
  const handleViewAllVehiclesEfficiency = async () => {
    setAllVehiclesModalVisible(true);
    setLoadingAllVehicles(true);

    try {
      const response = await fuelEfficiencyApi.getAllVehiclesHistory(30);
      setAllVehiclesData(response.data);
    } catch (error: any) {
      message.error(error?.message || t('error.fetching.data'));
    } finally {
      setLoadingAllVehicles(false);
    }
  };

  // Recalculate fuel efficiency for all vehicles (last 30 days)
  const handleRecalculateAllVehicles = async () => {
    setRecalculatingAllVehicles(true);
    try {
      const response = await fuelEfficiencyApi.recalculateAllVcomsat(30);
      const data = response.data;
      if (data && data.success !== false) {
        message.success(
          `Recalculated fuel efficiency for ${data.successCount}/${data.totalVehicles} vehicles (${data.totalCalculated} records)`
        );
      } else {
        message.warning(data?.message || 'Recalculation completed with issues');
      }
      // Refresh the all-vehicles data to show updated results
      const historyRes = await fuelEfficiencyApi.getAllVehiclesHistory(30);
      setAllVehiclesData(historyRes.data);
    } catch (error: any) {
      message.error(error?.response?.data?.message || error?.message || t('error.fetching.data'));
    } finally {
      setRecalculatingAllVehicles(false);
    }
  };

  // VCOMSAT Sync Handlers
  const desktopColumns = getVehicleDesktopColumns({
    t,
    onEdit: handleEdit,
    onDelete: handleDelete,
    onViewEfficiency: handleViewEfficiency
  });

  const mobileColumns = getVehicleMobileColumns({
    t,
    onEdit: handleEdit,
    onDelete: handleDelete,
    onViewEfficiency: handleViewEfficiency
  });

  const columns = isMobile ? mobileColumns : desktopColumns;

  // Header action buttons for mobile dropdown
  const headerMenuItems = [
    {
   key: 'efficiency',
      icon: <LineChartOutlined />,
      label: t('all.vehicles.efficiency'),
      onClick: handleViewAllVehiclesEfficiency
 },
    {
 key: 'capacity',
      icon: <SettingOutlined />,
      label: t('manage.capacity.tons'),
      onClick: () => window.location.href = '/vehicles/capacity-tons'
    },
    {
      key: 'sync-vehicles',
   icon: <SyncOutlined />,
      label: t('vcomsat.sync.vehicles'),
      onClick: handleSyncVehicles
    },
    {
      key: 'sync-journey',
      icon: <CloudSyncOutlined />,
      label: t('vcomsat.sync.journey'),
      onClick: handleOpenJourneySyncModal
    }
  ];

  return (
    <div style={{ padding: isMobile ? '12px' : '24px' }}>
      <Card bodyStyle={{ padding: isMobile ? 12 : 24 }}>
        <div style={{ 
       marginBottom: '16px', 
      display: 'flex', 
     flexDirection: isMobile ? 'column' : 'row',
          justifyContent: 'space-between', 
          alignItems: isMobile ? 'stretch' : 'center',
      gap: isMobile ? 12 : 0
        }}>
      <h2 style={{ margin: 0, fontSize: isMobile ? 18 : 24 }}>{t('vehicle.management')}</h2>
          
 {isMobile ? (
       <Space direction="vertical" style={{ width: '100%' }} size={8}>
              <Space style={{ width: '100%', justifyContent: 'space-between' }}>
    <Dropdown menu={{ items: headerMenuItems }} trigger={['click']}>
                  <Button icon={<MoreOutlined />}>
     {t('more.actions') || 'More'}
            </Button>
        </Dropdown>
      <Button
   type="primary"
         icon={<PlusOutlined />}
        onClick={handleAdd}
      >
       {t('add.vehicle')}
        </Button>
              </Space>
       </Space>
      ) : (
            <Space wrap>
          <Button
                type="default"
                icon={<LineChartOutlined />}
     onClick={handleViewAllVehiclesEfficiency}
     >
        {t('all.vehicles.efficiency')}
   </Button>
            <Link to="/vehicles/capacity-tons">
         <Button
     type="default"
        icon={<SettingOutlined />}
          >
                  {t('manage.capacity.tons')}
        </Button>
        </Link>
     <Button
         type="default"
                icon={<SyncOutlined />}
           onClick={handleSyncVehicles}
      loading={syncingVehicles}
       >
      {t('vcomsat.sync.vehicles')}
              </Button>
     <Button
       type="default"
           icon={<CloudSyncOutlined />}
      onClick={handleOpenJourneySyncModal}
       >
    {t('vcomsat.sync.journey')}
        </Button>
        <Button
          type="primary"
icon={<PlusOutlined />}
        onClick={handleAdd}
              >
             {t('add.vehicle')}
       </Button>
    </Space>
          )}
      </div>

        <Table
        columns={columns}
       dataSource={vehicles}
          rowKey="id"
          loading={loading}
 pagination={{ 
            pageSize: isMobile ? 5 : 10,
          size: isMobile ? 'small' : 'default',
       showSizeChanger: !isMobile,
    showTotal: isMobile ? undefined : (total) => `${t('total')} ${total}`
    }}
          scroll={{ x: isMobile ? undefined : true }}
          size={isMobile ? 'small' : 'middle'}
        />

        {/* Add/Edit Vehicle Modal */}
    <Modal
     title={editingVehicle ? t('edit.vehicle') : t('add.vehicle')}
   open={modalVisible}
onCancel={() => {
        setModalVisible(false);
            form.resetFields();
      }}
      onOk={() => form.submit()}
          width={isMobile ? '95%' : 600}
          style={{ top: isMobile ? 20 : undefined }}
   >
          <Form
   form={form}
            layout={isMobile ? 'vertical' : 'horizontal'}
            labelCol={isMobile ? undefined : { span: 6 }}
    wrapperCol={isMobile ? undefined : { span: 18 }}
onFinish={handleSubmit}
 >
            <Form.Item
       label={t('license.plate')}
  name="licensePlate"
 rules={[
   { required: true, message: t('required.field') },
   { pattern: /^[A-Za-z0-9]+$/, message: t('license.plate.alphanumeric.only') || 'License plate must contain only letters and numbers' }
 ]}
 normalize={(value: string) => value ? value.replace(/[^A-Za-z0-9]/g, '') : value}
    >
        <Input maxLength={20} />
        </Form.Item>

        <Form.Item
      label={t('brand')}
     name="brand"
     rules={[{ required: true, message: t('required.field') }]}
  >
      <Input maxLength={100} />
            </Form.Item>

            <Form.Item
    label={t('model')}
 name="model"
      rules={[{ required: true, message: t('required.field') }]}
            >
         <Input maxLength={100} />
     </Form.Item>

       <Form.Item
        label={t('year')}
         name="year"
       >
            <InputNumber
       min={1900}
     max={new Date().getFullYear() + 10}
                style={{ width: '100%' }}
      />
   </Form.Item>

 <Form.Item
       label={t('color')}
  name="color"
            >
              <Input maxLength={50} />
            </Form.Item>

 <Form.Item
       label={t('vehicle')}
     name="capacityTonsId"
   >
        <Select
        placeholder={t('please.select')}
  allowClear
           >
      {capacityTons.map(ct => (
 <Select.Option key={ct.id} value={ct.id}>
            {ct.capacity} tons {ct.description && `(${ct.description})`}
           </Select.Option>
    ))}
      </Select>
            </Form.Item>

            <Form.Item
              label={t('fuel.tank.capacity')}
          name="fuelTankCapacity"
         >
     <InputNumber
           min={0}
                precision={1}
     style={{ width: '100%' }}
    />
            </Form.Item>

      <Form.Item
        label={t('notes')}
   name="notes"
     >
          <Input.TextArea rows={3} />
  </Form.Item>
     </Form>
     </Modal>

        {/* Journey Sync Modal */}
        <JourneySyncModal
          visible={journeySyncModalVisible}
          onClose={() => setJourneySyncModalVisible(false)}
          onSync={handleSyncJourneyForAllVehicles}
          t={t}
          loading={syncingJourney}
          dateRange={journeyDateRange}
          onDateRangeChange={setJourneyDateRange}
          isMobile={isMobile}
        />

        {/* Fuel Efficiency History Modal */}
        <FuelEfficiencyModal
          visible={efficiencyModalVisible}
          onClose={() => setEfficiencyModalVisible(false)}
          t={t}
          selectedVehicle={selectedVehicle}
          loading={loadingEfficiency}
          efficiencySummary={efficiencySummary}
          efficiencyHistory={efficiencyHistory}
          isMobile={isMobile}
        />

  {/* All Vehicles Fuel Efficiency Modal */}
        <AllVehiclesEfficiencyModal
          visible={allVehiclesModalVisible}
          onClose={() => setAllVehiclesModalVisible(false)}
          t={t}
          loading={loadingAllVehicles}
          allVehiclesData={allVehiclesData}
          isMobile={isMobile}
          recalculating={recalculatingAllVehicles}
          onRecalculate={handleRecalculateAllVehicles}
        />
      </Card>
    </div>
  );
};

export default VehiclePage;